<template>
  <div class="reservationForm">
    <el-form
      ref="reservationForm"
      class="reservationForm"
      :model="form"
      :rules="rules"
      @submit.prevent="onSubmit"
    >
      <div class="reservationForm__form">
        <h4 v-if="isReadOnly">{{$t('records.reservation.form.title.readonly')}}</h4>
        <h4 v-else>{{ edit ? $t('records.reservation.form.edit') : $t('records.reservation.form.title.add') }}</h4>
        <p v-if="!isReadOnly && !edit">{{$t('records.reservation.form.subtitle')}}</p>

        <div
          v-if="currentRecord"
          class="reservationForm__form--details"
        >
          <p>{{$t('records.reservation.form.details.id')}} <b>{{currentRecord.number}}</b></p>
          <p>{{$t('records.reservation.form.details.date')}} <b>{{$moment(currentRecord.created_at).format('dddd Do MMMM YYYY')}} {{ $t('records.reservation.form.details.at') }} {{ $moment(currentRecord.created_at).format('HH') + $t('base.hourSeparator') + $moment(currentRecord.created_at).format('mm') }}</b></p>
          <p>{{$t('records.reservation.form.details.status')}} <b>{{currentRecord.state_label}}</b></p>
        </div>

        <hr class="reservationForm__separator">

        <!-- FROM & TO -->
        <div>
          <el-row :gutter="56">
            <el-col
              :sm="12"
              :lg="10"
            >
              <el-form-item
                :label="$t('records.reservation.form.desiredStartedAtLabel')"
                prop="desired_started_at"
              >
                <date-picker-input
                  v-model="form.desired_started_at"
                  :disabled-date="disabledDateFrom"
                  :value="form.desired_started_at"
                  :disabled="isReadOnly"
                  :placeholder="$t('records.reservation.form.desiredStartedAtPlaceholder')"
                >
                </date-picker-input>
              </el-form-item>
            </el-col>
            <el-col
              :sm="12"
              :lg="10"
            >
              <el-form-item
                :label="$t('records.reservation.form.desiredFinishedAtLabel')"
                prop="desired_finished_at"
              >
                <date-picker-input
                  v-model="form.desired_finished_at"
                  :disabled-date="disabledDateTo"
                  :value="form.desired_finished_at"
                  :disabled="!form.desired_started_at || isReadOnly"
                  :placeholder="$t('records.reservation.form.desiredFinishedAtPlaceholder')"
                >
                </date-picker-input>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <el-button
          v-if="isReadOnly || edit"
          class="reservationForm__modifyButton"
          @click="onEdit"
        >{{ edit ? $t('records.reservation.form.cancelEdit') : $t('records.reservation.form.edit') }}</el-button>

        <hr class="reservationForm__separator">

        <el-form-item prop="declaration_on_honour">
          <div :class="['checkboxFlex', form.declaration_on_honour ? 'checked' : '']">
            <el-checkbox
              v-model="form.declaration_on_honour"
              :disabled="isReadOnly"
            />
            <span @click="!isReadOnly ? form.declaration_on_honour = !form.declaration_on_honour : null">
              {{$t('records.reservation.form.declarationOnHonourLabel')}}
            </span>
          </div>
        </el-form-item>

        <el-form-item prop="accept_technical_diagnostic">
          <div :class="['checkboxFlex', form.accept_technical_diagnostic ? 'checked' : '']">
            <el-checkbox
              ref="accept_technical_diagnostic"
              v-model="form.accept_technical_diagnostic"
              :disabled="isReadOnly"
            />
            <span @click="!isReadOnly ? form.accept_technical_diagnostic = !form.accept_technical_diagnostic : null">
              {{$t('records.reservation.form.acceptTechnicalDiagnosticLabel')}}
            </span>
          </div>
        </el-form-item>

      </div>

      <div
        v-if="!isReadOnly"
        class="reservationForm__action"
      >
        <page>
          <el-row class="reservationForm__save-container">
            <el-col :sm="20">
              <p>
                {{ $t('records.reservation.form.save') }}
              </p>
            </el-col>
            <el-col :sm="4">
              <el-button
                class="reservationForm__save"
                type="secondary"
                :loading="loading"
                @click="onSubmit"
              >{{ $t('records.reservation.form.continueButton') }}</el-button>
            </el-col>
          </el-row>
        </page>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import DatePickerInput from "@/components/ui/DatePickerInput"

export default {
  name: 'ReservationForm',
  components: {
    DatePickerInput,
  },
  data: () => ({
    loading: false,
    edit: false,
    form: {
      desired_started_at: null,
      desired_finished_at: null,
      declaration_on_honour: null,
      accept_technical_diagnostic: true
    }
  }),
  computed: {
    ...mapState({
      currentRecord: state => state.records.currentRecord
    }),
    isReadOnly() {
      return (!!this.currentRecord.desired_started_at && !!this.currentRecord.desired_finished_at) && !this.edit
    },
    rules() {
      return {
        desired_started_at: { required: true, type: 'string', message: this.$t('records.reservation.form.desiredStartedAtInvalid') },
        desired_finished_at: { required: true, type: 'string', message: this.$t('records.reservation.form.desiredFinishedAtInvalid') },
        declaration_on_honour: { required: true, type: 'boolean', validator: this.declarationValidator },
        accept_technical_diagnostic: { required: false, type: 'boolean', message: this.$t('records.reservation.form.acceptTechnicalDiagnosticInvalid') }
      }
    }
  },
  mounted() {
    if (this.isReadOnly) {
      this.form.desired_started_at = this.currentRecord.desired_started_at
      this.form.desired_finished_at = this.currentRecord.desired_finished_at
      this.form.declaration_on_honour = true
      this.form.accept_technical_diagnostic = this.currentRecord.accept_technical_diagnostic
    }
  },
  methods: {
    disabledDateFrom(date) {
      return this.form.desired_finished_at ? date > new Date(this.form.desired_finished_at) : false
    },
    disabledDateTo(date) {
      return date < new Date(this.form.desired_started_at)
    },
    declarationValidator(rule, value, callback) {
      if (!value) {
        callback(new Error(this.$t('records.reservation.form.declarationOnHonourInvalid')))
      } else {
        callback()
      }
    },
    async onSubmit() {
      try {
        this.loading = true
        const validation = await this.$refs['reservationForm'].validate()

        if (this.form.declaration_on_honour && validation) {
          await this.$store.dispatch('records/updateRecord', { record: this.form, id: this.$route.params.id })

          this.$router.push({
            path: '/dossiers/' + this.currentRecord.id + '/ajout-de-locataire/'
          })

          this.$notify({
            type: 'success',
            title: this.$t('base.success.notificationTitle'),
            message: this.$t('records.reservation.form.message')
          })
        }

        if (this.edit) {
          this.edit = false
        }

        this.loading = false
      } catch (error) {
        this.loading = false
      }
    },
    onEdit() {
      if (!this.edit) {
        this.edit = true
        this.form = {
          desired_started_at: null,
          desired_finished_at: null,
          declaration_on_honour: true,
          accept_technical_diagnostic: true
        }
      } else if (this.edit) {
        this.edit = false
        this.form = {
          desired_started_at: this.currentRecord.desired_started_at,
          desired_finished_at: this.currentRecord.desired_finished_at,
          declaration_on_honour: true,
          accept_technical_diagnostic: true
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
$c: '.reservationForm';

#{$c} {
  margin-bottom: 24px;

  &__form > p {
    margin-bottom: 30px;
    margin-top: 0;
  }

  &__save-container {
    display: flex;
    align-items: center;
    justify-content: center;

    .el-col:nth-child(2) {
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
    }

    p {
      color: $--color-info;
    }
  }

  &__modifyButton {
    margin-bottom: 12px;
  }

  &__separator {
    border: 1px solid $--border-color-light;
  }

  .el-select,
  .el-date-editor {
    width: 100%;
  }

  .checkboxFlex {
    display: flex;
    align-items: center;

    span {
      line-height: 1.2;
      cursor: pointer;
      user-select: none;
      color: #606266;
    }
  }
  .checkboxFlex.checked span {
    color: $--color-primary;
    font-size: 16px;
  }
}
</style>